export const normalizeValue = (value: any, joinChar = ', '): string => {
  if (!value) return 'Not available';
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(joinChar) || 'Not available';
  }
  return String(value);
};

export const getFieldValue = (obj: any, fieldName: string, joinChar = ', '): string => {
  const value = obj?.[fieldName];
  return normalizeValue(value, joinChar);
};

export const cleanChemicalValue = (value: string): string => {
  if (!value) return 'Not available';
  let clean = value.replace(/^_+/, '').replace(/_+$/, '');
  clean = clean.split('___')[0];
  return clean || 'Not available';
};

export const getChemicalArrayValue = (arr: any): string => {
  if (!Array.isArray(arr) || arr.length === 0) return 'Not available';
  const first = arr[0];
  if (Array.isArray(first) && Array.isArray(first[0])) {
    return normalizeValue(first[0]);
  }
  return normalizeValue(first);
};

export const getProteinValue = (threeletterID: any, oneletterID: any, query: string): string => {
  const normalizedQuery = query.toLowerCase().replace(/\s+/g, ' ').trim();

  const matchingThreeLetter =
    Array.isArray(threeletterID) &&
    threeletterID.filter((id: string) =>
      id.toLowerCase().replace(/\s+/g, ' ').trim().includes(normalizedQuery),
    );

  const matchingOneLetter =
    Array.isArray(oneletterID) &&
    oneletterID.filter((id: string) =>
      id.toLowerCase().replace(/\s+/g, ' ').trim().includes(normalizedQuery),
    );

  if (matchingThreeLetter?.length > 0) {
    return normalizeValue(matchingThreeLetter);
  }
  if (matchingOneLetter?.length > 0) {
    return normalizeValue(matchingOneLetter);
  }

  return 'Not available';
};

export const getChromosomeCoord = (chromosome: any, coord: any): string => {
  if (chromosome && coord) {
    return `${chromosome}:${coord}`;
  }
  return 'Not available';
};

export const getResultLabel = (result: any, fieldNames: string[]): string => {
  for (const fieldName of fieldNames) {
    const value = result?.[fieldName];
    if (value) {
      return normalizeValue(value);
    }
  }
  return 'Not available';
};

// Función que replica exactamente la lógica original de getResultColumnValue
export const getResultColumnValue = (
  searchType: string,
  selectedFilter: string,
  result: any,
  query: string,
): string => {
  switch (searchType) {
    case 'DISEASES':
      return getDiseaseColumnValue(selectedFilter, result, query);
    case 'VARIANTS':
      return getVariantColumnValue(selectedFilter, result, query);
    case 'GENES':
      return getGeneColumnValue(selectedFilter, result);
    case 'CHEMICALS':
      return getCleanChemicalColumnValue(selectedFilter, result);
    default:
      return 'Not available';
  }
};

export const getDiseaseColumnValue = (selectedFilter: string, result: any, query: string): string => {
  switch (selectedFilter) {
    case 'diseaseUMLSCUI':
      return Array.isArray(result.diseaseUMLSCUI)
        ? result.diseaseUMLSCUI.join(', ')
        : result.diseaseUMLSCUI || 'Not available';
    case 'MSH':
      return Array.isArray(result.msh) ? result.msh.join(', ') : result.msh || 'Not available';
    case 'OMIM':
      return Array.isArray(result.omim)
        ? result.omim.find((i: string) => i === query) || result.omim.join(', ')
        : result.omim || 'Not available';
    case 'NCI':
      return Array.isArray(result.nci) ? result.nci.join(', ') : result.nci || 'Not available';
    case 'HPO':
      return Array.isArray(result.hpo)
        ? result.hpo.find((i: string) => i === query) || result.hpo.join(', ')
        : result.hpo || 'Not available';
    case 'MONDO':
      return Array.isArray(result.mondo)
        ? result.mondo.find((i: string) => i === query) || result.mondo.join(', ')
        : result.mondo || 'Not available';
    case 'EFO':
      return Array.isArray(result.efo)
        ? result.efo.find((i: string) => i === query) || result.efo.join(', ')
        : result.efo || 'Not available';
    case 'name':
      return result.term || 'Not available';
    case 'DO':
      return Array.isArray(result.do)
        ? result.do.find((i: string) => i === query) || result.do.join(', ')
        : result.do || 'Not available';
    default:
      return 'Not available';
  }
};

export const getVariantColumnValue = (selectedFilter: string, result: any, query: string): string => {
  switch (selectedFilter) {
    case 'strID':
      return Array.isArray(result.strID) ? result.strID.join(', ') : result.strID || 'Not available';
    case 'chromocoord':
      return result.chromosome ? `${result.chromosome}:${result.coord}` : 'Not available';
    case 'proteinStrID':
      return getProteinValue(result.threeletterID, result.oneletterID, query);
    default:
      return 'Not available';
  }
};

export const getGeneColumnValue = (selectedFilter: string, result: any): string => {
  switch (selectedFilter) {
    case 'symbol':
      return Array.isArray(result.symbol) ? result.symbol.join(', ') : result.symbol || 'Not available';
    case 'geneID':
      return Array.isArray(result.geneID) ? result.geneID.join(', ') : result.geneID || 'Not available';
    case 'ensemblID':
      return Array.isArray(result.ensembl_ids)
        ? result.ensembl_ids.join(', ')
        : result.ensembl_ids || 'Not available';
    case 'uniprotID':
      return Array.isArray(result.proteinStrIDs)
        ? result.proteinStrIDs.join(', ')
        : result.proteinStrIDs || 'Not available';
    case 'name':
      return result.term || result.matchedText || 'Not available';
    default:
      return 'Not available';
  }
};

export const getCleanChemicalColumnValue = (selectedFilter: string, result: any): string => {
  const label = getChemicalColumnValue(selectedFilter, result);
  let clean = typeof label === 'string' ? label.replace(/^_+/, '') : label;
  clean = typeof clean === 'string' ? clean.replace(/_+$/, '') : clean;
  clean = typeof clean === 'string' ? clean.split('___')[0] : clean;
  return clean || 'Not available';
};

export const getChemicalColumnValue = (selectedFilter: string, result: any): string => {
  switch (selectedFilter) {
    case 'CHEBI':
      return Array.isArray(result.chebi?.[0])
        ? result.chebi[0][0]?.join(', ')
        : result.chebi?.[0] || 'Not available';
    case 'DRUGBANK':
      return Array.isArray(result.drugbank?.[0])
        ? result.drugbank[0][0]?.join(', ')
        : result.drugbank?.[0] || 'Not available';
    case 'MESH':
      return Array.isArray(result.mesh?.[0])
        ? result.mesh[0][0]?.join(', ')
        : result.mesh?.[0] || 'Not available';
    case 'CHEMBL':
      return Array.isArray(result.chembl?.[0])
        ? result.chembl[0][0]?.join(', ')
        : result.chembl?.[0] || 'Not available';
    case 'PUBCHEM':
      return Array.isArray(result.pubchem?.[0])
        ? result.pubchem[0][0]?.join(', ')
        : result.pubchem?.[0] || 'Not available';
    case 'name':
      return result.term || 'Not available';
    default:
      return 'Not available';
  }
};

// Función que replica exactamente la lógica original de getResultName
export const getResultName = (result: any, searchType: string, selectedFilter: string): string => {
  switch (searchType) {
    case 'DISEASES':
      if (selectedFilter !== 'name') {
        return result.name || 'Not available';
      }
      return result.normalizedId || 'Not available';
    case 'VARIANTS':
      return result.geneSymbol || 'Not available';
    case 'GENES':
      if (selectedFilter !== 'name') {
        return result.geneID || 'Not available';
      }
      return result.normalizedId || 'Not available';
    case 'CHEMICALS':
      if (selectedFilter !== 'name') {
        return result.chemPrefName || 'Not available';
      }
      return result.normalizedId || result.chemID || 'Not available';
    default:
      return 'Not available';
  }
};

export const generateResultId = (
  result: any,
  selectedFilter: string,
  resultFields?: Record<string, string>,
): string => {
  // For name filters, use the normalized ID as the unique identifier
  if (selectedFilter === 'name') {
    const normalizedId = result?.normalizedId;
    if (normalizedId) {
      return String(normalizedId);
    }
    // Fallback to other unique identifiers
    const fallbackIds = [result?.diseaseUMLSCUI, result?.geneID, result?.chemID, result?.strID];
    for (const id of fallbackIds) {
      if (id) return String(id);
    }
    return '';
  }

  // For other filters, use the mapped field name from resultFields
  const fieldName = resultFields?.[selectedFilter] || selectedFilter;
  const primaryValue = result?.[fieldName];
  if (!primaryValue) return '';

  if (Array.isArray(primaryValue)) {
    return String(primaryValue[0]);
  }

  return String(primaryValue);
};
